import { Form, DateRangePicker } from "rsuite";
import PropTypes from "prop-types";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import advancedFormat from "dayjs/plugin/advancedFormat";
import { useRef } from "react";
import "./daterange.scss";
import { formatDateRange } from "../../utils/dateUtils";

dayjs.extend(advancedFormat);

const DateRange = ({
  value,
  setValue,
  label = "Date range",
  allowPastDates = false,
  maxYears = 2,
  ...props
}) => {
  const containerRef = useRef(null);

  // Custom function to disable dates outside allowed range
  const shouldDisableDate = (date) => {
    const today = dayjs().startOf("day");
    const twoYearsFromNow = dayjs()
      .add(maxYears, "year")
      .subtract(1, "day")
      .endOf("day");
    const checkDate = dayjs(date);

    if (allowPastDates) {
      // If allowPastDates is true, allow all past dates and next 2 years
      // Only disable dates beyond maxYears (default = 2) years in the future
      return checkDate.isAfter(twoYearsFromNow);
    }

    // If allowPastDates is false, allow only today and next 2 years
    // Disable past dates (before today) and dates beyond 2 years
    return checkDate.isBefore(today) || checkDate.isAfter(twoYearsFromNow);
  };

  const handleDateChange = (selectedRange) => {
    if (!selectedRange) {
      setValue([]);
      return;
    }

    const [start, end] = selectedRange;

    // Check if the dates are valid
    if (!dayjs(start).isValid() || !dayjs(end).isValid()) {
      toast.error("Invalid date selected!");
      return;
    }

    setValue(selectedRange);
  };

  return (
    <div {...props}>
      <Form.Group className="daterange-container">
        {label && (
          <Form.ControlLabel className="daterange">{label}</Form.ControlLabel>
        )}
        <div ref={containerRef}>
          <DateRangePicker
            value={value}
            onChange={handleDateChange}
            placeholder="Select date range"
            data-testid="date-range-picker"
            shouldDisableDate={shouldDisableDate}
            renderValue={formatDateRange}
            ranges={[]}
            cleanable={false}
            style={{ width: 230 }}
            container={() => containerRef.current}
          />
        </div>
      </Form.Group>
    </div>
  );
};

DateRange.propTypes = {
  value: PropTypes.arrayOf(PropTypes.instanceOf(Date)),
  setValue: PropTypes.func,
  label: PropTypes.string,
  allowPastDates: PropTypes.bool,
  maxYears: PropTypes.number,
};

export default DateRange;
